import {
  NewTag,
  AppliedDashboardButton,
  AppliedDashboardStatus,
  PickerContent,
  DashboardList,
  DashboardButton,
} from './styled';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Empty,
  Input,
  List,
  Modal,
  Spin,
  Tag,
  Typography,
  Tooltip,
  message,
} from 'antd';
import { StarOutlined, StarFilled, CloseOutlined } from '@ant-design/icons';
import { useSelector, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import usePersonalPresetsEnabled from '@src/hooks/usePersonalPresetsEnabled';
import presetStore from '@src/globalStore/presetsStore';
import {
  getUserProfile,
  selectedCustomerIdSelector,
  supportedMetricsSelector,
} from '@src/store/common/selectors';
import { allDimensionsListSelector } from '@src/store/common/dimensionList/selectors';
import { CURRENT_PRODUCT_MODE, DASHBOARD_TYPE } from '@src/utils/constants';
import {
  getPrivatePulse5Dashboards,
  getPublicPulse5Dashboards,
  getDashboardById,
} from '@src/api/dashboards';
import { getPersistence, putPersistence } from '@src/api/persistence';
import { getSupportedWidgetTypes } from '@src/components/widget-panel/config/utils';
import track from '@src/utils/sensor/track';
import { getDefaultView, usePresetView } from './usePresetView';
import {
  groupPresets,
  normalizePreset,
  PERSONAL_PRESETS_KEY,
  PresetSource,
  PresetViewConfig,
} from '@src/utils/presets/personalPresets';

interface Preferences {
  version: 1;
  pinnedDashboardIds: string[];
  hasApplied: boolean;
  hasOpened?: boolean;
}
const groupLabels = {
  'My presets': 'Favourites',
  'Conviva presets': 'Presets',
  'Saved dashboards': 'Saved dashboards',
};
const emptyPreferences: Preferences = { version: 1, pinnedDashboardIds: [], hasApplied: false };
const isCompatible = (dashboard) =>
  dashboard.productName === 'ei' &&
  (dashboard.dashboardType?.id ?? dashboard.dashboardTypeId) === DASHBOARD_TYPE.TRENDS;

interface PresetControls {
  showPicker: (entryPoint: 'layout_menu' | 'applied_status') => void;
  reset: () => void;
  appliedName?: string;
  discovery: boolean;
  busy: boolean;
}
const PresetContext = createContext<PresetControls | null>(null);

export function PresetDiscoveryBadge({ children }: { children: React.ReactNode }) {
  const controls = useContext(PresetContext);
  return (
    <Badge dot={Boolean(controls?.discovery)} color="blue">
      {children}
    </Badge>
  );
}

export function PresetLayoutAction({ onOpen }: { onOpen: () => void }) {
  const controls = useContext(PresetContext);
  if (!controls) return null;
  return (
    <Tooltip
      trigger={['hover', 'focus']}
      title="Use a dashboard’s layout, metrics, and dimensions. Your filters, dates, and comparisons stay unchanged."
    >
      <Badge dot={controls.discovery} color="blue">
        <Button
          onClick={() => {
            onOpen();
            controls.showPicker('layout_menu');
          }}
        >
          Import Layout <NewTag color="blue">New</NewTag>
        </Button>
      </Badge>
    </Tooltip>
  );
}

export function AppliedPresetStatus() {
  const controls = useContext(PresetContext);
  if (!controls?.appliedName) return null;
  const label = `Dashboard Applied: ${controls.appliedName}`;
  return (
    <AppliedDashboardStatus role="status">
      <Tooltip trigger={['hover', 'focus']} title={label}>
        <AppliedDashboardButton
          size="small"
          type="link"
          onClick={() => controls.showPicker('applied_status')}
          aria-label={label}
        >
          {label}
        </AppliedDashboardButton>
      </Tooltip>
      <Tooltip trigger={['hover', 'focus']} title="Reset dashboard changes">
        <Button
          size="small"
          type="text"
          disabled={controls.busy}
          onClick={controls.reset}
          aria-label="Reset dashboard changes"
          icon={<CloseOutlined />}
        />
      </Tooltip>
    </AppliedDashboardStatus>
  );
}

function PresetPicker({ customerId, profileId, children }) {
  const [open, setOpen] = useState(false);
  const [hasAppliedLocally, setHasAppliedLocally] = useState(false);
  const [hasOpenedLocally, setHasOpenedLocally] = useState(false);
  const discoverySaved = useRef(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [applying, setApplying] = useState<string | null>(null);
  const [applied, setApplied] = useState<{ name: string; before: PresetViewConfig } | null>(null);
  const request = useRef(0);
  const searchTracked = useRef(false);
  const mounted = useRef(true);
  const redux = useStore();
  const queryClient = useQueryClient();
  const { capture, apply } = usePresetView(() => setApplied(null));
  const presets = presetStore((state) => state.presets);
  const scope = [customerId, profileId, CURRENT_PRODUCT_MODE];
  const preferenceKey = ['personal-presets', ...scope];
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current += 1;
    };
  }, []);

  const preferences = useQuery(
    preferenceKey,
    async (): Promise<Preferences> => {
      try {
        const result = await getPersistence({ key: PERSONAL_PRESETS_KEY });
        return { ...emptyPreferences, ...result.data.configuration };
      } catch (err: any) {
        if (err.response?.status === 404) return emptyPreferences;
        throw err;
      }
    },
    { refetchOnWindowFocus: false },
  );
  const savePreferences = useMutation(
    async (configuration: Preferences) => {
      await putPersistence({ key: PERSONAL_PRESETS_KEY, configuration });
      return configuration;
    },
    {
      onSuccess: (configuration) => queryClient.setQueryData(preferenceKey, configuration),
      onError: () => {
        if (mounted.current)
          message.error('Your dashboard favourites could not be saved. Please try again.');
      },
    },
  );
  const persistPreferences = savePreferences.mutate;
  useEffect(() => {
    if (!hasOpenedLocally || !preferences.data || discoverySaved.current) return;
    discoverySaved.current = true;
    if (!preferences.data.hasOpened && !preferences.data.hasApplied) {
      persistPreferences({ ...preferences.data, hasOpened: true });
    }
  }, [hasOpenedLocally, preferences.data, persistPreferences]);

  const privateList = useQuery(
    ['preset-dashboards', 'private', ...scope],
    () => getPrivatePulse5Dashboards({}),
    { enabled: open, staleTime: 0 },
  );
  const publicList = useQuery(
    ['preset-dashboards', 'public', ...scope],
    () => getPublicPulse5Dashboards({}),
    { enabled: open, staleTime: 0 },
  );
  const pinned = preferences.data?.pinnedDashboardIds || [];
  const sources: PresetSource[] = [
    ...Object.values(presets || {})
      .filter((preset: any) => !preset.hide)
      .map((preset: any) => ({
        id: String(preset.key),
        name: preset.title,
        kind: 'conviva' as const,
        config: preset.config,
      })),
    ...[...(privateList.data?.data || []), ...(publicList.data?.data || [])]
      .filter(isCompatible)
      .map((dashboard) => ({
        id: String(dashboard.id),
        name: dashboard.name,
        kind: 'dashboard' as const,
        isPublic: Boolean(dashboard.isPublic),
      })),
  ];
  const groups = groupPresets(sources, pinned, search);
  const busy = applying !== null || savePreferences.isLoading;
  const sourceTrackingData = (source: PresetSource) => ({
    feature: 'Import Layout',
    source: source.kind,
    group:
      source.kind === 'conviva'
        ? 'presets'
        : pinned.includes(source.id)
          ? 'favourites'
          : 'saved_dashboards',
  });
  const showPicker = (entryPoint: 'layout_menu' | 'applied_status') => {
    setHasOpenedLocally(true);
    searchTracked.current = false;
    setSearch('');
    setError('');
    setOpen(true);
    track({
      event: 'Open Apply Preset',
      data: { feature: 'Import Layout', entry_point: entryPoint },
    });
  };
  const applySource = async (source: PresetSource) => {
    if (busy) return;
    const token = ++request.current;
    track({ event: 'Select Import Layout', data: sourceTrackingData(source) });
    setApplying(`${source.kind}:${source.id}`);
    setError('');
    try {
      let state = source.config;
      if (source.kind === 'dashboard') {
        const { data } = await getDashboardById(source.id);
        if (!data) {
          queryClient.invalidateQueries(['preset-dashboards']);
          throw new Error('This saved dashboard is no longer available.');
        }
        if ((data.dashboardType?.id ?? data.dashboardTypeId) !== DASHBOARD_TYPE.TRENDS) {
          throw new Error('This saved dashboard is no longer compatible with Video Trends.');
        }
        state = data.config?.state;
      }
      if (!mounted.current || token !== request.current) return;
      const currentState = redux.getState();
      if (
        getUserProfile(currentState).profile_id !== profileId ||
        selectedCustomerIdSelector(currentState) !== customerId
      )
        return;
      const dimensions = allDimensionsListSelector(currentState);
      const view = normalizePreset(state || {}, getDefaultView(), {
        metrics: Object.keys(supportedMetricsSelector(currentState)),
        dimensions: dimensions.map((dimension) => dimension.groupBy),
        widgetTypes: Object.values(getSupportedWidgetTypes()) as string[],
      });
      const before = applied?.before ?? capture();
      apply(view, before);
      setApplied({ name: source.name, before });
      setHasAppliedLocally(true);
      setOpen(false);
      track({ event: 'Apply Preset Success', data: sourceTrackingData(source) });
      if (preferences.data && !preferences.data.hasApplied) {
        savePreferences.mutate({ ...preferences.data, hasApplied: true });
      }
    } catch (err: any) {
      if (!mounted.current || token !== request.current) return;
      if (err.response?.status === 403 || err.response?.status === 404)
        queryClient.invalidateQueries(['preset-dashboards']);
      setError(
        err.response?.status === 403 || err.response?.status === 404
          ? 'This dashboard is no longer available. Choose another dashboard.'
          : err.message || 'Unable to apply this dashboard. Please try again.',
      );
      track({ event: 'Apply Preset Failure', data: sourceTrackingData(source) });
    } finally {
      if (mounted.current && token === request.current) setApplying(null);
    }
  };
  const pin = (source: PresetSource) => {
    if (!preferences.data || busy) return;
    const isPinned = pinned.includes(source.id);
    const data = { ...sourceTrackingData(source), action: isPinned ? 'remove' : 'favourite' };
    track({ event: isPinned ? 'Unpin Personal Preset' : 'Pin Personal Preset', data });
    savePreferences.mutate(
      {
        ...preferences.data,
        pinnedDashboardIds: isPinned
          ? pinned.filter((id) => id !== source.id)
          : [...pinned, source.id],
      },
      {
        onSuccess: () => track({ event: 'Import Layout Favourite Success', data }),
        onError: () => track({ event: 'Import Layout Favourite Failure', data }),
      },
    );
  };
  const reset = () => {
    if (!applied || busy) return;
    apply(applied.before);
    setApplied(null);
    track({ event: 'Undo Apply Preset', data: { feature: 'Import Layout' } });
  };
  return (
    <PresetContext.Provider
      value={{
        showPicker,
        reset,
        appliedName: applied?.name,
        busy,
        discovery:
          !hasOpenedLocally &&
          !hasAppliedLocally &&
          !preferences.isLoading &&
          !preferences.data?.hasOpened &&
          !preferences.data?.hasApplied,
      }}
    >
      {children}
      <Modal
        title="Select from Dashboards"
        open={open}
        footer={null}
        width={640}
        onCancel={() => {
          track({
            event: 'Close Import Layout',
            data: { feature: 'Import Layout', application_pending: applying !== null },
          });
          request.current += 1;
          setApplying(null);
          setOpen(false);
        }}
      >
        <PickerContent direction="vertical" size="middle">
          <Typography.Text type="secondary">
            Your filters, dates, and comparisons stay unchanged.
          </Typography.Text>
          <Input.Search
            aria-label="Search dashboards"
            placeholder="Search dashboards"
            value={search}
            onChange={(event) => {
              const value = event.target.value;
              setSearch(value);
              if (value.trim() && !searchTracked.current) {
                searchTracked.current = true;
                track({ event: 'Search Import Layout', data: { feature: 'Import Layout' } });
              }
            }}
            allowClear
          />
          {error && <Alert type="error" message={error} showIcon />}
          {preferences.isError && (
            <Alert
              type="warning"
              message="Personal shortcuts could not be loaded."
              action={<Button onClick={() => preferences.refetch()}>Retry</Button>}
            />
          )}
          {(privateList.isError || publicList.isError) && (
            <Alert
              type="warning"
              message="Some saved dashboards could not be loaded."
              action={
                <Button
                  onClick={() => {
                    privateList.refetch();
                    publicList.refetch();
                  }}
                >
                  Retry
                </Button>
              }
            />
          )}
          {(privateList.isFetching || publicList.isFetching) && <Spin size="small" />}
          <DashboardList>
            {Object.entries(groups).map(
              ([name, items]) =>
                items.length > 0 && (
                  <section key={name} aria-label={groupLabels[name]}>
                    <Typography.Title level={5}>{groupLabels[name]}</Typography.Title>
                    <List
                      dataSource={items}
                      renderItem={(source) => (
                        <List.Item
                          actions={
                            source.kind === 'dashboard'
                              ? [
                                  <Button
                                    key="pin"
                                    type="text"
                                    disabled={busy || !preferences.data}
                                    onClick={() => pin(source)}
                                    aria-label={`${pinned.includes(source.id) ? 'Remove from favourites' : 'Favourite'}: ${source.name}`}
                                    icon={
                                      pinned.includes(source.id) ? <StarFilled /> : <StarOutlined />
                                    }
                                  >
                                    {pinned.includes(source.id)
                                      ? 'Remove from favourites'
                                      : 'Favourite'}
                                  </Button>,
                                ]
                              : []
                          }
                        >
                          <DashboardButton
                            type="text"
                            disabled={busy || preferences.isLoading}
                            loading={applying === `${source.kind}:${source.id}`}
                            onClick={() => applySource(source)}
                          >
                            {source.name}{' '}
                            {source.kind === 'dashboard' && (
                              <Tag>{source.isPublic ? 'Public' : 'Private'}</Tag>
                            )}
                          </DashboardButton>
                        </List.Item>
                      )}
                    />
                  </section>
                ),
            )}
            {!Object.values(groups).some((items) => items.length) &&
              !privateList.isFetching &&
              !publicList.isFetching && <Empty description="No matching dashboards" />}
          </DashboardList>
        </PickerContent>
      </Modal>
    </PresetContext.Provider>
  );
}

export default function ApplyPreset({ children }: { children: React.ReactNode }) {
  const enabled = usePersonalPresetsEnabled();
  const { pathname } = useLocation();
  const { profile_id: profileId } = useSelector(getUserProfile);
  const customerId = useSelector(selectedCustomerIdSelector);
  if (!enabled || !profileId) return <>{children}</>;
  return (
    <PresetPicker
      key={`${pathname}:${customerId}:${profileId}`}
      customerId={customerId}
      profileId={profileId}
    >
      {children}
    </PresetPicker>
  );
}
